import React, { useEffect, useState } from 'react';
import { clinicService } from '../services/clinicService';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pagination, setPagination] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceItems, setInvoiceItems] = useState([{ description: '', quantity: 1, unit_price: 0 }]);
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();

  useEffect(() => {
    loadInvoices();
    loadPatients();
  }, [currentPage, searchTerm]);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await clinicService.getInvoices({ page: currentPage, search: searchTerm });
      setInvoices(response.data || []);
      setPagination(response.pagination || {});
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const response = await clinicService.getPatients({ per_page: 100 });
      setPatients(response.data || []);
    } catch (error) {
      console.error('Failed to load patients');
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const addInvoiceItem = () => {
    setInvoiceItems([...invoiceItems, { description: '', quantity: 1, unit_price: 0 }]);
  };

  const removeInvoiceItem = (index) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
  };

  const updateInvoiceItem = (index, field, value) => {
    const updated = [...invoiceItems];
    updated[index][field] = value;
    setInvoiceItems(updated);
  };

  const calculateSubtotal = () => {
    return invoiceItems.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const onSubmit = async (data) => {
    try {
      const subtotal = calculateSubtotal();
      const discount = parseFloat(data.discount || 0);
      const tax = parseFloat(data.tax || 0);
      
      await clinicService.createInvoice({
        ...data,
        items: invoiceItems,
        invoice_date: new Date().toISOString().split('T')[0]
      });
      
      toast.success('Invoice created successfully!');
      setShowModal(false);
      reset();
      setInvoiceItems([{ description: '', quantity: 1, unit_price: 0 }]);
      loadInvoices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create invoice');
    }
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
    setShowViewModal(true);
  };

  const handlePrint = (invoice) => {
    const printWindow = window.open('', '_blank');
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .header h1 { margin: 0; color: #333; }
          .invoice-details { margin: 20px 0; }
          .invoice-details table { width: 100%; }
          .invoice-details td { padding: 5px; }
          .items-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          .items-table th { background: #f8f9fa; }
          .totals { float: right; width: 300px; margin-top: 20px; }
          .totals table { width: 100%; }
          .totals td { padding: 8px; }
          .total-row { font-weight: bold; font-size: 1.1em; border-top: 2px solid #333; }
          .footer { margin-top: 50px; text-align: center; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>CLINIC INVOICE</h1>
          <p>Invoice #: ${invoice.invoice_number}</p>
        </div>
        
        <div class="invoice-details">
          <table>
            <tr>
              <td><strong>Patient:</strong> ${invoice.patient?.name}</td>
              <td><strong>Date:</strong> ${new Date(invoice.invoice_date).toLocaleDateString()}</td>
            </tr>
            <tr>
              <td><strong>Patient Code:</strong> ${invoice.patient?.patient_code}</td>
              <td><strong>Status:</strong> ${invoice.status.toUpperCase()}</td>
            </tr>
          </table>
        </div>
        
        <table class="items-table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Quantity</th>
              <th>Unit Price</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${invoice.items?.map(item => `
              <tr>
                <td>${item.description}</td>
                <td>${item.quantity}</td>
                <td>₹${parseFloat(item.unit_price).toFixed(2)}</td>
                <td>₹${parseFloat(item.amount).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <table>
            <tr>
              <td>Subtotal:</td>
              <td align="right">₹${parseFloat(invoice.subtotal).toFixed(2)}</td>
            </tr>
            <tr>
              <td>Discount:</td>
              <td align="right">-₹${parseFloat(invoice.discount).toFixed(2)}</td>
            </tr>
            <tr>
              <td>Tax:</td>
              <td align="right">₹${parseFloat(invoice.tax).toFixed(2)}</td>
            </tr>
            <tr class="total-row">
              <td>Total:</td>
              <td align="right">₹${parseFloat(invoice.total).toFixed(2)}</td>
            </tr>
            <tr>
              <td>Paid:</td>
              <td align="right">₹${parseFloat(invoice.paid_amount).toFixed(2)}</td>
            </tr>
            <tr>
              <td><strong>Balance:</strong></td>
              <td align="right"><strong>₹${(parseFloat(invoice.total) - parseFloat(invoice.paid_amount)).toFixed(2)}</strong></td>
            </tr>
          </table>
        </div>
        
        <div class="footer">
          <p>Thank you for your visit!</p>
        </div>
        
        <script>
          window.onload = () => {
            window.print();
            window.onafterprint = () => window.close();
          }
        </script>
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
  };

  const handlePayment = async (invoice) => {
    const amount = prompt(`Enter payment amount (Balance: ₹${(invoice.total - invoice.paid_amount).toFixed(2)}):`);
    if (amount) {
      try {
        const newPaidAmount = parseFloat(invoice.paid_amount) + parseFloat(amount);
        await clinicService.updateInvoice(invoice.id, { paid_amount: newPaidAmount });
        toast.success('Payment recorded successfully!');
        loadInvoices();
      } catch (error) {
        toast.error('Failed to record payment');
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this invoice?')) {
      try {
        await clinicService.deleteInvoice(id);
        toast.success('Invoice deleted successfully!');
        loadInvoices();
      } catch (error) {
        toast.error('Failed to delete invoice');
      }
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading && invoices.length === 0) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">💳 Invoices</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-semibold"
        >
          + Create Invoice
        </button>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <input
          type="text"
          placeholder="Search by invoice number or patient name..."
          value={searchTerm}
          onChange={handleSearch}
          className="w-full px-4 py-2 border rounded-lg"
        />
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-md overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {invoices.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                  No invoices found. Create your first invoice!
                </td>
              </tr>
            ) : (
              invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600">{invoice.invoice_number}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{invoice.patient?.name}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{new Date(invoice.invoice_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-semibold">₹{parseFloat(invoice.total).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-green-600">₹{parseFloat(invoice.paid_amount).toFixed(2)}</td>
                  <td className="px-6 py-4 whitespace-nowrap font-semibold text-red-600">
                    ₹{(parseFloat(invoice.total) - parseFloat(invoice.paid_amount)).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadge(invoice.status)}`}>
                      {invoice.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleView(invoice)}
                      className="text-green-600 hover:text-green-800 mr-3 font-medium"
                    >
                      👁️ View
                    </button>
                    <button
                      onClick={() => handlePrint(invoice)}
                      className="text-purple-600 hover:text-purple-800 mr-3 font-medium"
                    >
                      🖨️ Print
                    </button>
                    <button
                      onClick={() => handlePayment(invoice)}
                      className="text-blue-600 hover:text-blue-800 mr-3 font-medium"
                    >
                      💰 Pay
                    </button>
                    <button
                      onClick={() => handleDelete(invoice.id)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      🗑️ Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total} invoices
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="px-4 py-2 border rounded-lg bg-blue-50 text-blue-600 font-medium">
              Page {pagination.current_page} of {pagination.last_page}
            </span>
            <button
              onClick={() => setCurrentPage(p => Math.min(pagination.last_page, p + 1))}
              disabled={currentPage === pagination.last_page}
              className="px-4 py-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Create New Invoice</h2>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Patient *</label>
                <select {...register('patient_id', { required: 'Patient is required' })} className="w-full px-3 py-2 border rounded-lg">
                  <option value="">Select Patient</option>
                  {patients.map(patient => (
                    <option key={patient.id} value={patient.id}>
                      {patient.name} - {patient.patient_code}
                    </option>
                  ))}
                </select>
                {errors.patient_id && <span className="text-red-500 text-xs">{errors.patient_id.message}</span>}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold">Invoice Items</h3>
                  <button type="button" onClick={addInvoiceItem} className="text-green-600 hover:text-green-800 font-medium">
                    + Add Item
                  </button>
                </div>
                {invoiceItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 mb-2">
                    <input
                      type="text"
                      placeholder="Description"
                      value={item.description}
                      onChange={(e) => updateInvoiceItem(index, 'description', e.target.value)}
                      className="col-span-5 px-3 py-2 border rounded-lg"
                      required
                    />
                    <input
                      type="number"
                      placeholder="Qty"
                      value={item.quantity}
                      onChange={(e) => updateInvoiceItem(index, 'quantity', parseInt(e.target.value) || 0)}
                      className="col-span-2 px-3 py-2 border rounded-lg"
                      required
                      min="1"
                    />
                    <input
                      type="number"
                      placeholder="Unit Price"
                      value={item.unit_price}
                      onChange={(e) => updateInvoiceItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="col-span-3 px-3 py-2 border rounded-lg"
                      required
                      min="0"
                      step="0.01"
                    />
                    <div className="col-span-2 flex items-center">
                      <span className="font-semibold">₹{(item.quantity * item.unit_price).toFixed(2)}</span>
                      {invoiceItems.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeInvoiceItem(index)}
                          className="ml-2 text-red-600 hover:text-red-800"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-3 gap-4 border-t pt-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Discount (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('discount')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Tax (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('tax')}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Total</label>
                  <div className="px-3 py-2 bg-gray-100 rounded-lg font-bold text-lg">
                    ₹{(calculateSubtotal()).toFixed(2)}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Notes</label>
                <textarea {...register('notes')} className="w-full px-3 py-2 border rounded-lg" rows="2" />
              </div>

              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded-lg font-semibold">
                  Create Invoice
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    reset();
                    setInvoiceItems([{ description: '', quantity: 1, unit_price: 0 }]);
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Invoice Modal */}
      {showViewModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">Invoice Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-gray-500 hover:text-gray-700 text-2xl">
                ×
              </button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Invoice Number</label>
                  <p className="text-lg font-semibold text-blue-600">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadge(selectedInvoice.status)}`}>
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Patient</label>
                  <p className="text-lg">{selectedInvoice.patient?.name}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Date</label>
                  <p className="text-lg">{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Items</h3>
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items?.map((item, index) => (
                      <tr key={index} className="border-t">
                        <td className="px-4 py-2">{item.description}</td>
                        <td className="px-4 py-2">{item.quantity}</td>
                        <td className="px-4 py-2">₹{parseFloat(item.unit_price).toFixed(2)}</td>
                        <td className="px-4 py-2 font-semibold">₹{parseFloat(item.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-64">
                    <div className="flex justify-between py-2">
                      <span>Subtotal:</span>
                      <span className="font-semibold">₹{parseFloat(selectedInvoice.subtotal).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span>Discount:</span>
                      <span className="text-red-600">-₹{parseFloat(selectedInvoice.discount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2">
                      <span>Tax:</span>
                      <span>₹{parseFloat(selectedInvoice.tax).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-t font-bold text-lg">
                      <span>Total:</span>
                      <span>₹{parseFloat(selectedInvoice.total).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 text-green-600">
                      <span>Paid:</span>
                      <span className="font-semibold">₹{parseFloat(selectedInvoice.paid_amount).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between py-2 border-t font-bold text-red-600">
                      <span>Balance:</span>
                      <span>₹{(parseFloat(selectedInvoice.total) - parseFloat(selectedInvoice.paid_amount)).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {selectedInvoice.notes && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-500">Notes</label>
                  <p className="text-gray-700">{selectedInvoice.notes}</p>
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => handlePrint(selectedInvoice)}
                className="flex-1 bg-purple-600 hover:bg-purple-700 text-white py-2 rounded-lg font-semibold"
              >
                🖨️ Print Invoice
              </button>
              <button
                onClick={() => setShowViewModal(false)}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoices;
